// web/src/pages/IAdoAmor.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  updateDoc,
  doc,
  addDoc,
  getDoc,
  serverTimestamp,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

import { db, auth } from "../firebase/firebase";

import ChatBubble from "../components/chat/ChatBubble";
import ShortcutModal from "../components/chat/ShortcutModal";
import ShortcutDropdown from "../components/chat/ShortcutDropdown";
import RightSidebar from "../components/chat/RightSidebar";

import "./IAdoAmor.css";
import "./BaseLayout.css";

export default function IAdoAmor() {
  // ============================================================
  // AUTH / ADMIN
  // ============================================================
  const [authUser, setAuthUser] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);

  async function checkIsAdmin(uid) {
    if (!uid) return false;
    try {
      const ref = doc(db, "admins", uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) return false;
      const data = snap.data() || {};
      if (data.active === false) return false;
      return true;
    } catch {
      return false;
    }
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setAuthUser(u || null);

      if (!u) {
        setIsAdminUser(false);
        return;
      }

      const ok = await checkIsAdmin(u.uid);
      setIsAdminUser(ok);
    });

    return () => unsub();
  }, []);

  // ============================================================
  // 🔐 OPERADOR ATUAL (AGORA VEM DO AUTH)
  // - id = uid do Firebase Auth
  // - nome = displayName/email (fallback)
  // ============================================================
  const currentOperator = useMemo(() => {
    // fallback só para não quebrar UI enquanto auth carrega
    if (!authUser) {
      return {
        id: "anon",
        nome: "Operador",
      };
    }

    const nome =
      authUser.displayName ||
      authUser.email ||
      `Operador ${String(authUser.uid).slice(0, 6)}`;

    return {
      id: authUser.uid,
      nome,
    };
  }, [authUser]);

  // ============================================================
  // ESTADOS
  // ============================================================
  const [pendentes, setPendentes] = useState([]);
  const [minhasSessoes, setMinhasSessoes] = useState([]);
  const [outrosAtendimentos, setOutrosAtendimentos] = useState([]); // sessões de outros operadores
  const [finalizadas, setFinalizadas] = useState([]);

  const [operadores, setOperadores] = useState([]); // lista de operadores
  const [modalTransferir, setModalTransferir] = useState(false);
  const [selectedTransferOperator, setSelectedTransferOperator] = useState("");

  const [atual, setAtual] = useState(null);
  const [mensagens, setMensagens] = useState([]);
  const [msgInput, setMsgInput] = useState("");

  const [shortcuts, setShortcuts] = useState([]);
  const [isShortcutModalOpen, setIsShortcutModalOpen] = useState(false);
  const [showShortcutDropdown, setShowShortcutDropdown] = useState(false);
  const [filteredShortcuts, setFilteredShortcuts] = useState([]);
  const [selectedShortcutIndex, setSelectedShortcutIndex] = useState(0);

  const [userInfoCache, setUserInfoCache] = useState({});
  const [waitingTimes, setWaitingTimes] = useState({});
  const [activeSessionMinutes, setActiveSessionMinutes] = useState(0);

  const [historico, setHistorico] = useState([]); // histórico de sessões do usuário

  const mensagensUnsubRef = useRef(null);
  const chatEndRef = useRef(null);
  const prevCountRef = useRef(0);

  // ============================================================
  // ✅ (2) FINALIZADAS — COLAPSÁVEL + MODAL
  // ============================================================
  const [finalizadasExpanded, setFinalizadasExpanded] = useState(false);
  const [finalizadasModalOpen, setFinalizadasModalOpen] = useState(false);
  const [finalizadasSearch, setFinalizadasSearch] = useState("");

  const FINALIZADAS_PREVIEW_LIMIT = 5;

  const finalizadasFiltradas = useMemo(() => {
    const term = (finalizadasSearch || "").toLowerCase().trim();
    if (!term) return finalizadas;

    return finalizadas.filter((s) => {
      const nome = (s.avatarName || "").toLowerCase();
      const papel = (s.papel || "").toLowerCase();
      const userLabel = (renderUserLabel(s.userId) || "").toLowerCase();
      return (
        nome.includes(term) ||
        papel.includes(term) ||
        userLabel.includes(term) ||
        String(s.id || "").toLowerCase().includes(term)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalizadas, finalizadasSearch, userInfoCache]);

  const finalizadasPreview = useMemo(() => {
    if (finalizadasExpanded) return finalizadas;
    return finalizadas.slice(0, FINALIZADAS_PREVIEW_LIMIT);
  }, [finalizadas, finalizadasExpanded]);

  // ============================================================
  // 🔥 OPERADORES — STATUS ONLINE / BUSY
  // ============================================================
  useEffect(() => {
    const opRef = collection(db, "operators");

    const unsub = onSnapshot(opRef, (snap) => {
      const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setOperadores(lista);
    });

    return () => unsub();
  }, []);

  // Cria/pinga o operador atual como online
  useEffect(() => {
    // só registra presença quando tiver auth real
    if (!authUser || !currentOperator?.id || currentOperator.id === "anon")
      return;

    const ref = doc(db, "operators", currentOperator.id);

    // cria/atualiza o doc do operador
    setDoc(
      ref,
      {
        nome: currentOperator.nome,
        status: "online",
        lastPing: serverTimestamp(),
      },
      { merge: true }
    ).catch(() => {});

    const interval = setInterval(() => {
      setDoc(
        ref,
        {
          nome: currentOperator.nome,
          lastPing: serverTimestamp(),
        },
        { merge: true }
      ).catch(() => {});
    }, 10000);

    // ao sair da página, marca como offline (melhor esforço)
    const handleUnload = () => {
      setDoc(
        ref,
        {
          status: "offline",
          lastPing: serverTimestamp(),
        },
        { merge: true }
      ).catch(() => {});
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [authUser, currentOperator.id, currentOperator.nome]);

  // Atualiza status busy/online conforme quantidade de sessões
  useEffect(() => {
    if (!authUser || !currentOperator?.id || currentOperator.id === "anon")
      return;

    const ref = doc(db, "operators", currentOperator.id);
    const novoStatus = minhasSessoes.length > 0 ? "busy" : "online";

    setDoc(
      ref,
      {
        status: novoStatus,
        lastPing: serverTimestamp(),
      },
      { merge: true }
    ).catch(() => {});
  }, [authUser, currentOperator.id, minhasSessoes.length]);

  // ============================================================
  // 🔔 SOM DE NOVA SESSÃO
  // ============================================================
  const playNotification = () => {
    try {
      const audio = new Audio("/sounds/notify.mp3");
      audio.volume = 0.8;
      audio.play().catch(() => {});
    } catch (_) {}
  };

  // ============================================================
  // 🔥 1. PENDENTES
  // ============================================================
  useEffect(() => {
    const qP = query(
      collection(db, "ai_sessoes"),
      where("status", "==", "pendente")
    );

    const unsubPend = onSnapshot(qP, (snap) => {
      const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      lista.sort((a, b) => {
        const ta = a.createdAt?.seconds || 0;
        const tb = b.createdAt?.seconds || 0;
        return ta - tb;
      });

      if (lista.length > prevCountRef.current) {
        playNotification();
      }
      prevCountRef.current = lista.length;

      setPendentes(lista);
    });

    return () => unsubPend();
  }, []);

  // TEMPO DE ESPERA
  useEffect(() => {
    if (pendentes.length === 0) {
      setWaitingTimes({});
      return;
    }

    const intervalId = setInterval(() => {
      const agora = Date.now();
      const novos = {};

      pendentes.forEach((s) => {
        let minutos = 0;

        if (s.createdAt?.seconds) {
          const criadoMs =
            s.createdAt.seconds * 1000 +
            (s.createdAt.nanoseconds || 0) / 1_000_000;

          minutos = Math.floor((agora - criadoMs) / 60000);
        }
        novos[s.id] = minutos;
      });

      setWaitingTimes(novos);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [pendentes]);

  // ============================================================
  // 🔥 2. MINHAS SESSÕES (aceitas pelo operador atual)
  // ============================================================
  useEffect(() => {
    if (!authUser || !currentOperator?.id || currentOperator.id === "anon") {
      setMinhasSessoes([]);
      return;
    }

    const q = query(
      collection(db, "ai_sessoes"),
      where("status", "==", "aceita"),
      where("operadorId", "==", currentOperator.id)
    );

    const unsub = onSnapshot(q, (snap) => {
      const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      lista.sort((a, b) => {
        const ta = a.iniciouEm?.seconds || 0;
        const tb = b.iniciouEm?.seconds || 0;
        return ta - tb;
      });

      setMinhasSessoes(lista);
    });

    return () => unsub();
  }, [authUser, currentOperator.id]);

  // ============================================================
  // 🔥 2.1 SESSÕES DE OUTROS OPERADORES (fila avançada)
  // ============================================================
  useEffect(() => {
    const q = query(
      collection(db, "ai_sessoes"),
      where("status", "==", "aceita")
    );

    const unsub = onSnapshot(q, (snap) => {
      const todas = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      const outras = todas.filter(
        (s) => s.operadorId && s.operadorId !== currentOperator.id
      );

      outras.sort((a, b) => {
        const ta = a.iniciouEm?.seconds || 0;
        const tb = b.iniciouEm?.seconds || 0;
        return ta - tb;
      });

      setOutrosAtendimentos(outras);
    });

    return () => unsub();
  }, [currentOperator.id]);

  // ============================================================
  // 🔥 FINALIZADAS
  // ============================================================
  useEffect(() => {
    if (!authUser || !currentOperator?.id || currentOperator.id === "anon") {
      setFinalizadas([]);
      return;
    }

    const q = query(
      collection(db, "ai_sessoes"),
      where("status", "==", "finalizada"),
      where("operadorId", "==", currentOperator.id)
    );

    const unsub = onSnapshot(q, (snap) => {
      const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      lista.sort((a, b) => {
        const ta = a.finalizadoEm?.seconds || 0;
        const tb = b.finalizadoEm?.seconds || 0;
        return tb - ta;
      });

      setFinalizadas(lista);
    });

    return () => unsub();
  }, [authUser, currentOperator.id]);

  // ============================================================
  // 🔥 4. ATALHOS
  // ============================================================
  useEffect(() => {
    const q = query(collection(db, "ai_shortcuts"));
    const unsub = onSnapshot(q, (snap) => {
      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.command || "").localeCompare(b.command || ""));

      setShortcuts(lista);
    });

    return () => unsub();
  }, []);

  // ============================================================
  // INFO DO USUÁRIO (app)
  // ============================================================
  async function fetchUserInfo(userId) {
    if (!userId) return;

    if (userInfoCache[userId]) return userInfoCache[userId];

    try {
      const snap = await getDoc(doc(db, "users", userId));

      const info = snap.exists()
        ? { name: snap.data().name || "", email: snap.data().email || "" }
        : { name: "", email: "" };

      setUserInfoCache((prev) => ({ ...prev, [userId]: info }));
      return info;
    } catch {
      setUserInfoCache((prev) => ({
        ...prev,
        [userId]: { name: "", email: "" },
      }));
      return { name: "", email: "" };
    }
  }

  useEffect(() => {
    const all = [
      ...pendentes,
      ...minhasSessoes,
      ...outrosAtendimentos,
      ...finalizadas,
    ];
    const unique = [...new Set(all.map((s) => s.userId).filter(Boolean))];

    const toFetch = unique.filter((id) => !userInfoCache[id]);
    if (toFetch.length === 0) return;

    (async () => {
      const cache = {};
      for (const id of toFetch) {
        try {
          const snap = await getDoc(doc(db, "users", id));
          cache[id] = snap.exists()
            ? { name: snap.data().name || "", email: snap.data().email || "" }
            : { name: "", email: "" };
        } catch {
          cache[id] = { name: "", email: "" };
        }
      }
      setUserInfoCache((prev) => ({ ...prev, ...cache }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendentes, minhasSessoes, outrosAtendimentos, finalizadas]);

  function renderUserLabel(userId) {
    const info = userInfoCache[userId];
    if (!info) return userId || "—";
    return [info.name, info.email].filter(Boolean).join(" • ") || userId;
  }

  // ============================================================
  // HISTÓRICO DO USUÁRIO (para RightSidebar / estilo Intercom)
  // ============================================================
  useEffect(() => {
    if (!atual?.userId) {
      setHistorico([]);
      return;
    }

    const q = query(
      collection(db, "ai_sessoes"),
      where("userId", "==", atual.userId)
    );

    const unsub = onSnapshot(q, (snap) => {
      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((s) => s.id !== atual.id) // ignora a sessão atual
        .sort((a, b) => {
          const ta =
            a.finalizadoEm?.seconds ||
            a.iniciouEm?.seconds ||
            a.createdAt?.seconds ||
            0;
          const tb =
            b.finalizadoEm?.seconds ||
            b.iniciouEm?.seconds ||
            b.createdAt?.seconds ||
            0;
          return tb - ta;
        });

      setHistorico(lista);
    });

    return () => unsub();
  }, [atual?.userId, atual?.id]);

  // ============================================================
  // ABRIR SESSÃO / ESCUTAR MENSAGENS
  // ============================================================
  function abrirSessao(sessao) {
    setAtual(sessao);

    if (sessao.userId) fetchUserInfo(sessao.userId);

    if (mensagensUnsubRef.current) mensagensUnsubRef.current();

    const msgsRef = collection(db, "ai_sessoes", sessao.id, "mensagens");

    mensagensUnsubRef.current = onSnapshot(msgsRef, (snap) => {
      const lista = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.timestamp?.seconds || 0;
          const tb = b.timestamp?.seconds || 0;
          return ta - tb;
        });
      setMensagens(lista);
    });
  }

  useEffect(() => {
    return () => {
      if (mensagensUnsubRef.current) mensagensUnsubRef.current();
    };
  }, []);

  // ============================================================
  // ASSUMIR (de pendente -> aceita)
  // ============================================================
  async function assumirSessao(sessao) {
    if (!authUser) {
      alert("Faça login para assumir atendimentos.");
      return;
    }

    const ref = doc(db, "ai_sessoes", sessao.id);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
      alert("Sessão não encontrada.");
      return;
    }

    const data = snap.data();

    if (
      data.operadorId &&
      data.operadorId !== currentOperator.id &&
      !isAdminUser
    ) {
      alert("Essa sessão já foi assumida por outro atendente.");
      return;
    }

    await updateDoc(ref, {
      status: "aceita",
      operadorId: currentOperator.id,
      operadorNome: currentOperator.nome,
      iniciouEm: serverTimestamp(),
    });

    const novoSnap = await getDoc(ref);
    abrirSessao({ id: sessao.id, ...novoSnap.data() });
  }

  // ============================================================
  // TRANSFERIR SESSÃO
  // ============================================================
  async function confirmarTransferencia() {
    if (!atual || !selectedTransferOperator) return;

    if (!authUser) {
      alert("Faça login para transferir atendimentos.");
      return;
    }

    // dono OU admin pode transferir
    const isOwnerLocal =
      !atual.operadorId || atual.operadorId === currentOperator.id;
    const canTransfer = isOwnerLocal || isAdminUser;

    if (!canTransfer) {
      alert("Você só pode transferir sessões que estão sob seu atendimento.");
      return;
    }

    const ref = doc(db, "ai_sessoes", atual.id);

    try {
      if (selectedTransferOperator === "fila") {
        // devolve para fila de pendentes
        await updateDoc(ref, {
          status: "pendente",
          operadorId: null,
          operadorNome: null,
          transferidoPorId: currentOperator.id,
          transferidoPorNome: currentOperator.nome,
          transferidoEm: serverTimestamp(),
          transferidoPorAdmin: !!isAdminUser,
        });

        setAtual(null);
        setMensagens([]);
        if (mensagensUnsubRef.current) mensagensUnsubRef.current();
      } else {
        const op = operadores.find((o) => o.id === selectedTransferOperator);

        await updateDoc(ref, {
          status: "aceita",
          operadorId: op?.id,
          operadorNome: op?.nome || op?.name || "Atendente",
          transferidoPorId: currentOperator.id,
          transferidoPorNome: currentOperator.nome,
          transferidoEm: serverTimestamp(),
          transferidoPorAdmin: !!isAdminUser,
        });

        setAtual((prev) =>
          prev
            ? {
                ...prev,
                operadorId: op?.id,
                operadorNome: op?.nome || op?.name || "Atendente",
              }
            : prev
        );
      }
    } catch (e) {
      console.error("Erro ao transferir sessão", e);
      alert("Não foi possível transferir a sessão.");
    }

    setModalTransferir(false);
    setSelectedTransferOperator("");
  }

  // ============================================================
  // ENVIAR MENSAGEM
  // ============================================================
  async function enviarMensagem() {
    if (!msgInput.trim() || !atual) return;

    if (!authUser) {
      alert("Faça login para responder atendimentos.");
      return;
    }

    // dono OU admin pode responder
    const isOwnerLocal =
      !atual.operadorId || atual.operadorId === currentOperator.id;
    const canReply = isOwnerLocal || isAdminUser;

    if (!canReply) {
      alert(
        "Você está apenas visualizando esta sessão. Peça transferência para responder."
      );
      return;
    }

    await addDoc(collection(db, "ai_sessoes", atual.id, "mensagens"), {
      texto: msgInput.trim(),
      de: "admin",
      origem: "painel",
      operadorId: currentOperator.id,
      operadorNome: currentOperator.nome,
      enviadoPorAdmin: !!isAdminUser,
      timestamp: serverTimestamp(),
    });

    setMsgInput("");
    setShowShortcutDropdown(false);
  }

  // ============================================================
  // FINALIZAR
  // ============================================================
  async function finalizarAtendimento() {
    if (!atual) return;

    if (!authUser) {
      alert("Faça login para finalizar atendimentos.");
      return;
    }

    // dono OU admin pode finalizar
    const isOwnerLocal =
      !atual.operadorId || atual.operadorId === currentOperator.id;
    const canFinish = isOwnerLocal || isAdminUser;

    if (!canFinish) {
      alert("Você só pode finalizar atendimentos que estão sob seu atendimento.");
      return;
    }

    await updateDoc(doc(db, "ai_sessoes", atual.id), {
      status: "finalizada",
      finalizadoEm: serverTimestamp(),
      finalizadoPorId: currentOperator.id,
      finalizadoPorNome: currentOperator.nome,
      finalizadoPorAdmin: !!isAdminUser,
    });

    setAtual(null);
    setMensagens([]);
    if (mensagensUnsubRef.current) mensagensUnsubRef.current();
  }

  // ============================================================
  // NOTA INTERNA (RightSidebar)
  // ============================================================
  async function salvarNotaInterna(nota) {
    if (!atual) return;

    try {
      const ref = doc(db, "ai_sessoes", atual.id);
      await updateDoc(ref, {
        notaInterna: nota || "",
      });

      setAtual((prev) =>
        prev
          ? {
              ...prev,
              notaInterna: nota || "",
            }
          : prev
      );
    } catch (e) {
      console.error("Erro ao salvar nota interna", e);
      alert("Não foi possível salvar a nota interna.");
    }
  }

  // ============================================================
  // ATALHOS
  // ============================================================
  async function salvarAtalho({ id, title, command, message }) {
    const payload = {
      title: title.trim(),
      command: command.replace("/", "").trim(),
      message: message.trim(),
      updatedAt: serverTimestamp(),
    };

    if (!payload.command || !payload.message) return;

    if (id) {
      await updateDoc(doc(db, "ai_shortcuts", id), payload);
    } else {
      await addDoc(collection(db, "ai_shortcuts"), {
        ...payload,
        createdAt: serverTimestamp(),
      });
    }
  }

  async function deletarAtalho(id) {
    await deleteDoc(doc(db, "ai_shortcuts", id));
  }

  // ============================================================
  // HORÁRIO / FORMATAÇÃO
  // ============================================================
  function formatHora(timestamp) {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // Scroll automático
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [mensagens]);

  // ============================================================
  // DROPDOWN DO ATALHO
  // ============================================================
  function aplicarAtalho(atalho) {
    setMsgInput(atalho.message || "");
    setShowShortcutDropdown(false);
  }

  function handleInputChange(e) {
    const value = e.target.value;
    setMsgInput(value);

    if (value.startsWith("/")) {
      const termo = value.slice(1).toLowerCase();

      const filtrados = shortcuts.filter((s) => {
        const cmd = (s.command || "").toLowerCase();
        const title = (s.title || "").toLowerCase();
        return cmd.includes(termo) || title.includes(termo);
      });

      setFilteredShortcuts(filtrados);
      setShowShortcutDropdown(filtrados.length > 0);
      setSelectedShortcutIndex(0);
    } else {
      setShowShortcutDropdown(false);
    }
  }

  function handleInputKeyDown(e) {
    if (showShortcutDropdown) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedShortcutIndex((i) =>
          i + 1 >= filteredShortcuts.length ? 0 : i + 1
        );
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedShortcutIndex((i) =>
          i - 1 < 0 ? filteredShortcuts.length - 1 : i - 1
        );
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        aplicarAtalho(filteredShortcuts[selectedShortcutIndex]);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      enviarMensagem();
    }
  }

  // ============================================================
  // TEMPO DE ATENDIMENTO
  // ============================================================
  useEffect(() => {
    if (!atual?.iniciouEm?.seconds) {
      setActiveSessionMinutes(0);
      return;
    }

    const interval = setInterval(() => {
      const inicioMs =
        atual.iniciouEm.seconds * 1000 +
        (atual.iniciouEm.nanoseconds || 0) / 1_000_000;

      const diff = Date.now() - inicioMs;
      setActiveSessionMinutes(Math.floor(diff / 60000));
    }, 1000);

    return () => clearInterval(interval);
  }, [atual]);

  function getSLAColor(min) {
    if (min <= 2) return "#2ecc71";
    if (min <= 5) return "#f39c12";
    return "#e74c3c";
  }

  function getOperatorStatusLabel(status) {
    if (status === "busy") return "Ocupado";
    if (status === "offline") return "Offline";
    return "Online";
  }

  // ============================================================
  // UI — PADRONIZADO NO LAYOUT DO PAINEL
  // ============================================================
  const isOwner =
    atual && (!atual.operadorId || atual.operadorId === currentOperator.id);

  // ✅ Admin pode moderar qualquer sessão (encerrar / transferir / responder)
  const canModerate = !!atual && (isOwner || isAdminUser);

  const currentUserInfo =
    atual?.userId && userInfoCache[atual.userId]
      ? userInfoCache[atual.userId]
      : null;

  const timeline = {
    minutesMs: activeSessionMinutes * 60000,
    total:
      activeSessionMinutes <= 0
        ? "menos de 1 minuto"
        : ` ${activeSessionMinutes} minuto${
            activeSessionMinutes === 1 ? "" : "s"
          } `,
  };

  return (
    <div className="ia-page-container">
      {/* HEADER */}
      <header className="ia-header">
        <div className="ia-title-block">
          <h1 className="titulo">Atendimentos – IA do Amor 💗</h1>
          <p className="ia-subtitle">
            Acompanhe em tempo real as conversas que chegam do app One Love.
          </p>

          {/* info discreta do operador */}
          <p className="ia-subtitle" style={{ marginTop: 6, opacity: 0.9 }}>
            Operador: <b>{currentOperator.nome}</b>{" "}
            {isAdminUser ? (
              <span style={{ marginLeft: 8 }}>• ✅ ADMIN</span>
            ) : null}
          </p>
        </div>

        <div className="ia-header-stats">
          <div className="ia-pill ia-pill-pendente">
            Pendentes: <b>{pendentes.length}</b>
          </div>
          <div className="ia-pill ia-pill-ativa">
            Minhas ativas: <b>{minhasSessoes.length}</b>
          </div>
          <div className="ia-pill ia-pill-ativa">
            Ativas de outros: <b>{outrosAtendimentos.length}</b>
          </div>
          <div className="ia-pill ia-pill-finalizada">
            Finalizadas: <b>{finalizadas.length}</b>
          </div>
        </div>
      </header>

      <div className="layout">
        {/* ======================================================== */}
        {/* ESQUERDA */}
        {/* ======================================================== */}
        <aside className="col-esquerda">
          {/* Operador atual */}
          <div className="operador-header">
            <div className="operador-avatar">
              {currentOperator.nome.charAt(0)}
            </div>
            <div>
              <p className="operador-nome">{currentOperator.nome}</p>
              <p className="operador-status">
                <span className="status-bolinha" />
                {minhasSessoes.length > 0 ? " Ocupado" : " Online"}
                {isAdminUser ? " • Admin" : ""}
              </p>
            </div>
          </div>

          {/* Outros operadores */}
          <div className="lista-bloco operadores-list">
            <div className="lista-titulo-row">
              <h2 className="subtitulo">Outros atendentes</h2>
            </div>

            {operadores
              .filter((op) => op.id !== currentOperator.id)
              .map((op) => (
                <div key={op.id} className="operator-row">
                  <div className="operator-avatar-mini">
                    {(op.nome || op.name || "A").charAt(0)}
                  </div>
                  <div className="operator-info">
                    <p className="operator-name">
                      {op.nome || op.name || op.id}
                    </p>
                    <p
                      className={`operator-status operator-${
                        op.status || "online"
                      }`}
                    >
                      {getOperatorStatusLabel(op.status)}
                    </p>
                  </div>
                </div>
              ))}

            {operadores.filter((op) => op.id !== currentOperator.id).length ===
              0 && (
              <p className="vazio">
                Nenhum outro atendente conectado no momento.
              </p>
            )}
          </div>

          <div className="divider" />

          {/* Minhas sessões */}
          <div className="lista-bloco">
            <div className="lista-titulo-row">
              <h2 className="subtitulo">Minhas sessões</h2>
              {minhasSessoes.length > 0 && (
                <span className="badge-count">{minhasSessoes.length}</span>
              )}
            </div>

            {minhasSessoes.length === 0 && (
              <p className="vazio">Nenhuma sessão em andamento.</p>
            )}

            {minhasSessoes.map((s) => (
              <div
                key={s.id}
                className={`sessao-card minhas ${
                  atual?.id === s.id ? "sessao-card-ativa" : ""
                }`}
                onClick={() => abrirSessao(s)}
              >
                <div className="sessao-header-line">
                  <span className="sessao-avatar-mini">
                    {s.avatarName?.charAt(0) || "A"}
                  </span>
                  <span className="sessao-nome">{s.avatarName}</span>
                </div>
                <div className="info">
                  <p>
                    <b>Usuário:</b> {renderUserLabel(s.userId)}
                  </p>
                  <p>
                    <b>Estilo:</b> {s.papel}
                  </p>
                </div>
                <div className="sessao-footer-line">
                  <span className="tag tag-ativa">Em andamento</span>
                  <button
                    className="btn-abrir btn-abrir-mini"
                    onClick={(e) => {
                      e.stopPropagation();
                      abrirSessao(s);
                    }}
                  >
                    Abrir chat
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="divider" />

          {/* Sessões de outros atendentes */}
          <div className="lista-bloco">
            <div className="lista-titulo-row">
              <h2 className="subtitulo">Sessões de outros atendentes</h2>
              {outrosAtendimentos.length > 0 && (
                <span className="badge-count badge-others">
                  {outrosAtendimentos.length}
                </span>
              )}
            </div>

            {outrosAtendimentos.length === 0 && (
              <p className="vazio">Nenhuma sessão de outros atendentes.</p>
            )}

            {outrosAtendimentos.map((s) => (
              <div
                key={s.id}
                className={`sessao-card outras ${
                  atual?.id === s.id ? "sessao-card-ativa" : ""
                }`}
                onClick={() => abrirSessao(s)}
              >
                <div className="sessao-header-line">
                  <span className="sessao-avatar-mini outras-mini">
                    {s.avatarName?.charAt(0) || "A"}
                  </span>
                  <span className="sessao-nome">
                    {s.avatarName}{" "}
                    <span className="sessao-owner">
                      ({s.operadorNome || "Atendente"})
                    </span>
                  </span>
                </div>
                <div className="info">
                  <p>
                    <b>Usuário:</b> {renderUserLabel(s.userId)}
                  </p>
                  <p>
                    <b>Estilo:</b> {s.papel}
                  </p>
                </div>
                <span className="tag tag-visualizando">Visualização</span>
              </div>
            ))}
          </div>

          <div className="divider" />

          {/* Pendentes */}
          <div className="lista-bloco">
            <div className="lista-titulo-row">
              <h2 className="subtitulo">Pendentes</h2>
              {pendentes.length > 0 && (
                <span className="badge-count badge-pendente">
                  {pendentes.length}
                </span>
              )}
            </div>

            {pendentes.length === 0 && (
              <p className="vazio">Nenhuma solicitação no momento.</p>
            )}

            {pendentes.map((s) => {
              const waitMin = waitingTimes[s.id] ?? 0;
              return (
                <div key={s.id} className="sessao-card pendente">
                  <div className="sessao-header-line">
                    <span className="sessao-avatar-mini">
                      {s.avatarName?.charAt(0) || "A"}
                    </span>
                    <span className="sessao-nome">{s.avatarName}</span>
                  </div>

                  <div className="info">
                    <p>
                      <b>Usuário:</b> {renderUserLabel(s.userId)}
                    </p>
                    <p>
                      <b>Estilo:</b> {s.papel}
                    </p>
                  </div>

                  <div
                    className="tempo-espera"
                    style={{ color: getSLAColor(waitMin) }}
                  >
                    ⏳ {waitMin} min esperando
                  </div>

                  <button
                    className="btn-aceitar"
                    onClick={() => assumirSessao(s)}
                  >
                    Assumir atendimento
                  </button>
                </div>
              );
            })}
          </div>

          <div className="divider" />

          {/* ======================================================== */}
          {/* ✅ FINALIZADAS — COLAPSÁVEL + PREVIEW + MODAL */}
          {/* ======================================================== */}
          <div className="lista-bloco">
            <div className="lista-titulo-row finalizadas-header-row">
              <div className="finalizadas-title">
                <h2 className="subtitulo">Finalizadas</h2>
                {finalizadas.length > 0 && (
                  <span className="badge-count badge-finalizada">
                    {finalizadas.length}
                  </span>
                )}
              </div>

              {finalizadas.length > 0 && (
                <div className="finalizadas-actions">
                  <button
                    className="finalizadas-toggle-btn"
                    onClick={() => setFinalizadasExpanded((v) => !v)}
                    type="button"
                    title={
                      finalizadasExpanded
                        ? "Mostrar menos"
                        : `Mostrar todas (${finalizadas.length})`
                    }
                  >
                    {finalizadasExpanded ? "Mostrar menos" : "Mostrar todas"}
                  </button>

                  <button
                    className="finalizadas-open-modal-btn"
                    onClick={() => {
                      setFinalizadasSearch("");
                      setFinalizadasModalOpen(true);
                    }}
                    type="button"
                    title={`Ver todas as finalizadas (${finalizadas.length})`}
                  >
                    Ver todas ({finalizadas.length})
                  </button>
                </div>
              )}
            </div>

            {finalizadas.length === 0 && (
              <p className="vazio">Nenhum atendimento finalizado ainda.</p>
            )}

            {/* Preview (últimas 5) OU todas se expanded */}
            {finalizadasPreview.map((s) => (
              <div
                key={s.id}
                className="sessao-card finalizada"
                onClick={() => abrirSessao(s)}
              >
                <div className="sessao-header-line">
                  <span className="sessao-avatar-mini finalizada-mini">
                    {s.avatarName?.charAt(0) || "A"}
                  </span>
                  <span className="sessao-nome">{s.avatarName}</span>
                </div>
                <div className="info">
                  <p>
                    <b>Usuário:</b> {renderUserLabel(s.userId)}
                  </p>
                  <p>
                    <b>Estilo:</b> {s.papel}
                  </p>
                </div>
                <span className="tag tag-finalizada">Encerrada</span>
              </div>
            ))}

            {/* Hint quando está colapsado e existe mais */}
            {!finalizadasExpanded &&
              finalizadas.length > FINALIZADAS_PREVIEW_LIMIT && (
                <div className="finalizadas-hint">
                  Mostrando {FINALIZADAS_PREVIEW_LIMIT} de {finalizadas.length}.
                  <button
                    className="finalizadas-link"
                    type="button"
                    onClick={() => {
                      setFinalizadasSearch("");
                      setFinalizadasModalOpen(true);
                    }}
                  >
                    Ver todas
                  </button>
                </div>
              )}
          </div>
        </aside>

        {/* ======================================================== */}
        {/* DIREITA — CHAT + RIGHT SIDEBAR (INTERCOM STYLE) */}
        {/* ======================================================== */}
        <section className="col-direita">
          {/* COLUNA CENTRAL — CHAT */}
          <div className="chat-main-column">
            {!atual && (
              <div className="chat-placeholder">
                <p>Selecione uma sessão à esquerda para visualizar o chat.</p>
                <p className="chat-placeholder-sub">
                  Quando um usuário escolher um guia no app, a conversa aparece
                  aqui em tempo real.
                </p>
              </div>
            )}

            {atual && (
              <div className="chat-box">
                <div className="chat-header">
                  <div className="chat-header-info">
                    <div className="avatar-circle">
                      {atual.avatarName?.charAt(0) || "A"}
                    </div>
                    <div>
                      <h2>{atual.avatarName}</h2>
                      <p>
                        Usuário: <b>{renderUserLabel(atual.userId)}</b> • Estilo:{" "}
                        <span>{atual.papel}</span>
                      </p>

                      {atual.operadorNome && (
                        <p className="chat-operador-info">
                          Atendido por: <b>{atual.operadorNome}</b>
                        </p>
                      )}

                      {!isOwner && isAdminUser && (
                        <p
                          className="chat-operador-info"
                          style={{ opacity: 0.9 }}
                        >
                          ✅ Você está como <b>ADMIN</b> (pode moderar esta
                          sessão).
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="chat-header-actions">
                    <span className="tag tag-ativa">
                      {isOwner
                        ? atual.status === "aceita"
                          ? "Em atendimento"
                          : "Visualizando"
                        : isAdminUser
                        ? "Admin"
                        : "Somente leitura"}
                    </span>

                    {canModerate && (
                      <button
                        className="btn-transferir"
                        onClick={() => setModalTransferir(true)}
                      >
                        Transferir
                      </button>
                    )}

                    {canModerate && (
                      <button
                        className="btn-finalizar"
                        onClick={finalizarAtendimento}
                      >
                        Finalizar
                      </button>
                    )}
                  </div>
                </div>

                <div
                  className="chat-time-banner"
                  style={{ color: getSLAColor(activeSessionMinutes) }}
                >
                  —
                  {activeSessionMinutes <= 0
                    ? " Conversando há menos de 1 minuto "
                    : ` Conversando há ${activeSessionMinutes} minuto${
                        activeSessionMinutes === 1 ? "" : "s"
                      } `}
                  —
                </div>

                <div className="chat-mensagens">
                  {mensagens.map((msg) => (
                    <ChatBubble
                      key={msg.id}
                      texto={msg.texto}
                      tipo={msg.de === "admin" ? "admin" : "user"}
                      hora={formatHora(msg.timestamp)}
                    />
                  ))}
                  <div ref={chatEndRef} />
                </div>

                <div className="chat-input-wrapper">
                  <div className="chat-input-area">
                    <input
                      type="text"
                      placeholder='Digite sua mensagem... (use "/" para atalhos)'
                      value={msgInput}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                      disabled={!canModerate}
                    />

                    <button
                      className="btn-enviar"
                      onClick={enviarMensagem}
                      disabled={!canModerate}
                    >
                      Enviar
                    </button>

                    {showShortcutDropdown && filteredShortcuts.length > 0 && (
                      <ShortcutDropdown
                        shortcuts={filteredShortcuts}
                        selectedIndex={selectedShortcutIndex}
                        onSelect={aplicarAtalho}
                      />
                    )}
                  </div>

                  <div className="chat-shortcut-bar">
                    <button
                      className="btn-shortcut"
                      onClick={() => setIsShortcutModalOpen(true)}
                    >
                      + Adicionar atalho
                    </button>
                    <span className="chat-shortcut-hint">
                      Dica: digite <code>/</code> para ver mensagens salvas.
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* COLUNA DIREITA — PAINEL INTERCOM STYLE */}
          <RightSidebar
            atual={atual}
            userInfo={currentUserInfo}
            historico={historico}
            isOwner={!!canModerate}
            onFinalizar={finalizarAtendimento}
            onTransferir={() => setModalTransferir(true)}
            onSalvarNota={salvarNotaInterna}
            timeline={timeline}
          />
        </section>
      </div>

      {/* MODAL DE ATALHOS */}
      {isShortcutModalOpen && (
        <ShortcutModal
          shortcuts={shortcuts}
          onClose={() => setIsShortcutModalOpen(false)}
          onSave={salvarAtalho}
          onDelete={deletarAtalho}
        />
      )}

      {/* MODAL DE TRANSFERÊNCIA */}
      {modalTransferir && atual && (
        <div className="ia-modal-backdrop">
          <div className="ia-modal">
            <h3>Transferir atendimento</h3>
            <p>
              Escolha para qual atendente deseja transferir a sessão ou devolva
              para a fila.
            </p>

            <label className="ia-modal-label">Destino da transferência</label>
            <select
              className="ia-modal-select"
              value={selectedTransferOperator}
              onChange={(e) => setSelectedTransferOperator(e.target.value)}
            >
              <option value="">Selecione...</option>
              <option value="fila">Devolver para fila de pendentes</option>
              {operadores
                .filter((op) => op.id !== currentOperator.id)
                .map((op) => (
                  <option key={op.id} value={op.id}>
                    {(op.nome || op.name || op.id) +
                      " — " +
                      getOperatorStatusLabel(op.status)}
                  </option>
                ))}
            </select>

            <div className="ia-modal-actions">
              <button
                className="btn-modal-cancel"
                onClick={() => {
                  setModalTransferir(false);
                  setSelectedTransferOperator("");
                }}
              >
                Cancelar
              </button>
              <button
                className="btn-modal-confirm"
                onClick={confirmarTransferencia}
                disabled={!selectedTransferOperator}
              >
                Transferir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* ✅ MODAL — TODAS AS FINALIZADAS */}
      {/* ======================================================== */}
      {finalizadasModalOpen && (
        <div className="finalizadas-modal-backdrop">
          <div className="finalizadas-modal">
            <div className="finalizadas-modal-header">
              <div>
                <h3>Finalizadas</h3>
                <p>
                  Total: <b>{finalizadas.length}</b>
                </p>
              </div>

              <button
                type="button"
                className="finalizadas-modal-close"
                onClick={() => setFinalizadasModalOpen(false)}
                aria-label="Fechar"
                title="Fechar"
              >
                ✕
              </button>
            </div>

            <div className="finalizadas-modal-search">
              <input
                type="text"
                placeholder="Buscar por nome, estilo, usuário, e-mail..."
                value={finalizadasSearch}
                onChange={(e) => setFinalizadasSearch(e.target.value)}
              />
            </div>

            <div className="finalizadas-modal-list">
              {finalizadasFiltradas.length === 0 && (
                <p className="finalizadas-modal-empty">
                  Nenhum resultado encontrado.
                </p>
              )}

              {finalizadasFiltradas.map((s) => (
                <div
                  key={s.id}
                  className="sessao-card finalizada finalizada-modal-card"
                  onClick={() => {
                    abrirSessao(s);
                    setFinalizadasModalOpen(false);
                  }}
                >
                  <div className="sessao-header-line">
                    <span className="sessao-avatar-mini finalizada-mini">
                      {s.avatarName?.charAt(0) || "A"}
                    </span>
                    <span className="sessao-nome">{s.avatarName}</span>
                  </div>
                  <div className="info">
                    <p>
                      <b>Usuário:</b> {renderUserLabel(s.userId)}
                    </p>
                    <p>
                      <b>Estilo:</b> {s.papel}
                    </p>
                  </div>
                  <span className="tag tag-finalizada">Encerrada</span>
                </div>
              ))}
            </div>

            <div className="finalizadas-modal-footer">
              <button
                type="button"
                className="finalizadas-modal-secondary"
                onClick={() => setFinalizadasModalOpen(false)}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
